import { CdkDragMove } from '@angular/cdk/drag-drop';
import { ChangeDetectorRef, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';

import { Subject, takeUntil } from 'rxjs';

import { UiConfigurationViewer } from '@mirage-projects/cw-commons';

import { LayoutViewerService } from './layout-viewer.service';
/**
 * This component has the viewer layout.
 *
 * It's composed of four main parts.
 */
@Component({
    selector: 'cw-layout-viewer',
    templateUrl: './layout-viewer.component.html',
    styleUrls: ['./layout-viewer.component.scss'],
    standalone: false,
})
export class LayoutViewerComponent implements OnInit, OnDestroy {
    @Input() projectId: number | undefined;
    /** Configuration for the layout viewer to show the required layout*/
    @Input() configurationViewer: UiConfigurationViewer | undefined;

    /** @deprecated */
    @ViewChild('widgetGrid ') widgetGrid!: ElementRef;

    readonly DEFAULT_PANEL_SIZE = 360;
    readonly MIN_PANEL_SIZE = 300;
    readonly MAX_PANEL_SIZE = 500;
    readonly MIN_WIDGET_SIZE = 100;
    widthLeftPanel = this.DEFAULT_PANEL_SIZE;
    widthRightPanel = this.DEFAULT_PANEL_SIZE;

    private initialLeftPanelPositionX = 0;
    private initialRightPanelPositionX = 0;
    private initialWidgetPanelPosition = 0;
    private canvasEventsDisabled = false;
    private sizeMainWidget = 0;
    private onDestroy$: Subject<void> = new Subject();

    constructor(
        public layoutViewerService: LayoutViewerService,
        private cdr: ChangeDetectorRef
    ) {}

    ngOnInit(): void {
        this.layoutViewerService.splitedView$.pipe(takeUntil(this.onDestroy$)).subscribe(() => {
            this.resizeWidgetView();
        });
        if (this.configurationViewer?.showLeftPanelCollapsed) {
            this.layoutViewerService.collapseLeftPanel();
        }
        if (this.configurationViewer?.showRightPanelCollapsed) {
            this.layoutViewerService.collapseRightPanel();
        }
    }

    ngOnDestroy(): void {
        this.onDestroy$.next();
    }

    /** @ignore */
    disableCanvasEvents(): void {
        this.canvasEventsDisabled = true;
    }

    /** @ignore */
    enableCanvasEvents(): void {
        this.canvasEventsDisabled = false;
    }

    /** @ignore */
    areCanvasEventsDisabled(): boolean {
        return this.canvasEventsDisabled;
    }

    dragLeftPanelStarted(): void {
        this.initialLeftPanelPositionX = this.widthLeftPanel;

        this.disableCanvasEvents();
    }

    /** @ignore */
    resizeLeftPanel(e: CdkDragMove<any>): void {
        this.enableCanvasEvents();

        const newWidth = this.initialLeftPanelPositionX + e.distance.x;
        const needResize = newWidth !== this.widthLeftPanel && newWidth > this.MIN_PANEL_SIZE && newWidth < this.MAX_PANEL_SIZE;

        if (needResize) {
            this.widthLeftPanel = newWidth;
        }

        this.resetDragElementTransformStyle(e.source.element.nativeElement);
    }

    /** @ignore */
    startResizeRightPanel(): void {
        this.initialRightPanelPositionX = this.widthRightPanel;

        this.disableCanvasEvents();
    }

    /** @ignore */
    resizeRightPanel(e: CdkDragMove<any>): void {
        this.enableCanvasEvents();

        const newWidth = this.initialRightPanelPositionX - e.distance.x;
        if (newWidth > this.MIN_PANEL_SIZE && newWidth < this.MAX_PANEL_SIZE) {
            this.widthRightPanel = newWidth;
        }

        this.resetDragElementTransformStyle(e.source.element.nativeElement);
    }

    /** @ignore */
    startResizeWidget(): void {
        this.initialWidgetPanelPosition = this.sizeMainWidget;

        this.disableCanvasEvents();
    }

    /** @ignore */
    resizeWidget(e: CdkDragMove<any>): void {
        this.enableCanvasEvents();

        const offset = this.isHorizontalLayout() ? e.distance.y : e.distance.x;
        const newSize = this.initialWidgetPanelPosition + offset;
        const sizeGrid = this.isHorizontalLayout() ? this.widgetGrid.nativeElement.offsetHeight : this.widgetGrid.nativeElement.offsetWidth;
        if (newSize > this.MIN_WIDGET_SIZE && sizeGrid - newSize > this.MIN_WIDGET_SIZE) {
            this.sizeMainWidget = newSize;
        }

        this.resetDragElementTransformStyle(e.source.element.nativeElement);
    }

    /** @ignore */
    resizeWidgetView(): void {
        if (this.layoutViewerService.splitedView) {
            const size = this.isHorizontalLayout() ? this.widgetGrid.nativeElement.offsetHeight : this.widgetGrid.nativeElement.offsetWidth;
            this.sizeMainWidget = size / 2;
        }
        this.cdr.detectChanges();
    }

    /** @ignore */
    dragOver(e: DragEvent): void {
        e.preventDefault();
    }

    /** @ignore */
    isHorizontalLayout(): boolean {
        return this.configurationViewer?.layoutViews !== 'row';
    }

    /** @ignore */
    showLeftPanel(): boolean {
        return this.configurationViewer?.leftPanel !== 'forbidden';
    }

    /** @ignore */
    showRightPanel(): boolean {
        return this.configurationViewer?.rightPanel !== 'forbidden';
    }

    get positionButtonLeft(): number {
        return this.layoutViewerService.expandedLeftPanel ? this.widthLeftPanel : 5;
    }

    get iconExpandLeftPanel(): string {
        return this.layoutViewerService.expandedLeftPanel ? 'acc-left' : 'acc-right';
    }

    get positionButtonRight(): number {
        return this.layoutViewerService.expandedRightPanel ? this.widthRightPanel : 5;
    }

    get iconExpandRightPanel(): string {
        return this.layoutViewerService.expandedRightPanel ? 'acc-right' : 'acc-left';
    }

    get templateWidthWidget(): string {
        return this.layoutViewerService.splitedView ? `${this.sizeMainWidget}px 5px auto` : 'auto';
    }
    get templateWidget(): any {
        return this.isHorizontalLayout()
            ? { 'grid-template-rows': this.templateWidthWidget }
            : { 'grid-template-columns': this.templateWidthWidget };
    }

    /**
     * cdkDrag adds a transform style that makes the drag bar show into an incorrect position
     * @param element Element to clear the transform css style
     */
    private resetDragElementTransformStyle(element: HTMLElement): void {
        element.style.transform = '';
    }
}
